import { Sparkle } from 'lucide-react'

import { Card } from '@/components/ui/card'
import { PricingFeatureItem } from '../landing/PricingFeatureItem'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { Switch } from '@/components/ui/switch'
import { useTranslation } from '@/hooks/useTranslation'

interface BillingToggleProps {
	billingPeriod: 'monthly' | 'yearly'
	onChange: (period: 'monthly' | 'yearly') => void
	className?: string
	savingsPercentage?: number
}

export function BillingToggle({
	billingPeriod,
	onChange,
	className,
	savingsPercentage = 17,
}: BillingToggleProps) {
	const { t } = useTranslation()
	return (
		<div className={cn('flex items-center gap-4 justify-center', className)}>
			<span
				className={cn(
					'font-medium',
					billingPeriod === 'monthly' && 'text-foreground',
					billingPeriod === 'yearly' && 'text-muted-foreground'
				)}>
				{t('pricingCard.monthly')}
			</span>
			<Switch
				aria-label={t('pricingCard.yearly')}
				checked={billingPeriod === 'yearly'}
				onCheckedChange={(checked) => onChange(checked ? 'yearly' : 'monthly')}
			/>
			<span
				className={cn(
					'font-medium',
					billingPeriod === 'yearly' && 'text-foreground',
					billingPeriod === 'monthly' && 'text-muted-foreground'
				)}>
				{t('pricingCard.yearly')}
				{savingsPercentage > 0 && (
					<span className="ml-1.5 text-sm text-emerald-600 font-medium">
						{t('pricingCard.save', { percentage: savingsPercentage })}
					</span>
				)}
			</span>
		</div>
	)
}

interface PricingCardProps {
	name: string
	monthlyPrice: number | string
	yearlyPrice: number | string
	monthlyPriceId: string
	yearlyPriceId: string
	billingPeriod: 'monthly' | 'yearly'
	isMostPopular: boolean
	features: string[]
	className?: string
	hideButton?: boolean
	button?: React.ReactNode
	additionalPrice?: string
	savingsPercentage?: number
	seatSelector?: React.ReactNode
}

export function PricingCard({
	name,
	monthlyPrice: monthly,
	yearlyPrice: yearly,
	monthlyPriceId,
	yearlyPriceId,
	billingPeriod,
	isMostPopular,
	features,
	className,
	hideButton,
	button,
	additionalPrice,
	savingsPercentage,
	seatSelector,
}: PricingCardProps) {
	const { t } = useTranslation()
	const price = billingPeriod === 'monthly' ? monthly : yearly
	const priceId = billingPeriod === 'monthly' ? monthlyPriceId : yearlyPriceId
	const isEnterprise = typeof price === 'string'

	return (
		<Card
			className={cn(
				'relative rounded-2xl shadow-none flex flex-col py-8 px-7 gap-7 border',
				className
			)}>
			<div className="flex flex-col gap-3">
				<div className="flex items-center justify-between">
					<h4 className="font-heading text-2xl font-semibold text-foreground">
						{name}
					</h4>
					{isMostPopular === true && (
						<span className="rounded-full bg-primary px-3 text-center text-sm font-semibold text-primary-foreground py-1 inline-flex items-center gap-1">
							<Sparkle size={16} className="fill-primary-foreground" />
							{t('pricingCard.launchingOffer')}
						</span>
					)}
				</div>
				<div>
					{typeof price === 'number' ? (
						<>
							<span className="font-heading text-5xl font-semibold">
								${price}
							</span>
							<span className="text-muted-foreground font-semibold ml-2 text-xl font-heading">
								{billingPeriod === 'monthly' ? t('pricingCard.perMonth') : t('pricingCard.perYear')}
							</span>
							{billingPeriod === 'yearly' && typeof monthly === 'number' && (
								<div className="text-sm text-muted-foreground mt-1">
									{t('pricingCard.monthlyBilledMonthly', { price: monthly })}
									{savingsPercentage && savingsPercentage > 0 && (
										<span className="ml-1.5 text-emerald-600 font-medium">
											{t('pricingCard.saveWithPercentage', { percentage: savingsPercentage })}
										</span>
									)}
								</div>
							)}
						</>
					) : (
						<span className="font-heading text-3xl font-semibold">{price}</span>
					)}
				</div>
			</div>
			<div className="flex flex-col gap-8">
				<ul className="space-y-2">
					{features.map((feature, index) => (
						<PricingFeatureItem key={index} text={feature} />
					))}
				</ul>
			</div>

			<div className="flex flex-col gap-8">
				{seatSelector}
				{!hideButton && (
					<>
						{button || (
							<Button size="lg" asChild className="font-semibold">
								<a href="#" data-price-id={priceId}>
									{isEnterprise ? t('pricingCard.contactUsPlain') : t('pricingCard.getStarted')}
								</a>
							</Button>
						)}
					</>
				)}
				{additionalPrice && (
					<p className="text-sm text-muted-foreground text-center">
						{additionalPrice}
					</p>
				)}
			</div>
		</Card>
	)
}
